import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '@mui/material/styles';
import { Observer } from 'mobx-react';
import { toast } from 'react-toastify';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Button } from '@shared/components/Button';
import { ToastMessage } from '@shared/components/Toast';
import { useCompanyUIStore } from '@core/useStores';
import { ImageCropModal } from 'Procedures/components/ProcedureModal/components/ImageCropModal';

import { useStyles } from './CompanyLogoView.styles';
import { ThemeProps } from '@styles/theme';
import { messages } from 'Companies/companies.messages';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const CompanyLogoView: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const companyUIStore = useCompanyUIStore();

    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const [cropMimeType, setCropMimeType] = useState<string>('image/jpeg');
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);

    const getLogo = async () => {
        companyUIStore.getLogo(companyUIStore.companyToEditId!);
    };

    useEffect(() => {
        getLogo();
    });

    const onDeleteLogo = async () => {
        const result = await companyUIStore.deleteLogo(companyUIStore.companyToEditId!);
        if (result.success) {
            toast.success(<ToastMessage message={messages['companyLogo.removal.toast.success']} type='success' />);
            getLogo();
        } else {
            toast.error(<ToastMessage message={messages['companyLogo.removal.toast.error']} type='error' />);
        }
    };

    const onUploadLogo = async (file: File) => {
        const result = await companyUIStore.uploadLogo(companyUIStore.companyToEditId!, file);
        if (result.success) {
            toast.success(<ToastMessage message={messages['companyLogo.upload.toast.success']} type='success' />);
            getLogo();
        } else {
            toast.error(<ToastMessage message={messages['companyLogo.upload.toast.error']} type='error' />);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
        maxSize: MAX_FILE_SIZE,
        multiple: false,
        noKeyboard: true,
        onDropAccepted: (files: File[]) => {
            const rawUrl = URL.createObjectURL(files[0]);
            setCropSrc(rawUrl);
            setCropMimeType(files[0].type || 'image/jpeg');
            setIsCropModalOpen(true);
        },
        onDropRejected: () => {
            toast.error(<ToastMessage message={messages['companyLogo.error.tooLarge']} type='error' />);
        },
    });

    const handleCropComplete = (blob: Blob, previewUrl: string) => {
        if (cropSrc) {URL.revokeObjectURL(cropSrc);}
        setCropSrc(null);
        setIsCropModalOpen(false);
        const ext = cropMimeType === 'image/png' ? 'png' : cropMimeType === 'image/webp' ? 'webp' : 'jpg';
        const croppedFile = new File([blob], `company-logo.${ext}`, { type: cropMimeType });
        onUploadLogo(croppedFile).then(() => {
            URL.revokeObjectURL(previewUrl);
        });
    };

    const handleCropCancel = () => {
        if (cropSrc) {URL.revokeObjectURL(cropSrc);}
        setCropSrc(null);
        setIsCropModalOpen(false);
    };

    return (
        <Observer>
            {() => {
                const logo = companyUIStore.companyLogo;
                let imageSrc: string | null = null;
                if (logo) {
                    const decodedImage = atob(logo);
                    const imageDecoder = decodedImage.indexOf('svg') >= 0 ? 'svg+xml' : 'png';
                    imageSrc = `data:image/${imageDecoder};base64,${logo}`;
                }

                return (
                    <div className={styles.wrapper}>
                        <div
                            className={styles.imageZone}
                            title={logo ? messages['companyLogo.buttons.updateLogo'] : messages['companyLogo.buttons.uploadNew']}
                            {...getRootProps()}
                        >
                            <input {...getInputProps()} />
                            {imageSrc ? (
                                <img className={styles.imageThumb} src={imageSrc} alt="company logo" data-testid="avatar-image" />
                            ) : (
                                <div className={styles.imagePlaceholder}>
                                    <CloudUploadOutlinedIcon style={{ fontSize: 36, opacity: 0.25, color: theme.colors.grey_text }} />
                                </div>
                            )}
                            <div className={styles.imageOverlay}>
                                <CloudUploadOutlinedIcon style={{ fontSize: 26 }} />
                                <span>{logo ? messages['companyLogo.buttons.updateLogo'] : messages['companyLogo.buttons.uploadNew']}</span>
                            </div>
                        </div>

                        {logo && (
                            <Button
                                onClick={onDeleteLogo}
                                color="secondary"
                                size="small"
                                startIcon={<DeleteOutlineIcon />}
                                data-testid="user-avatar-view-remove-button"
                            >
                                {messages['companyLogo.buttons.remove']}
                            </Button>
                        )}

                        {cropSrc && (
                            <ImageCropModal
                                isOpen={isCropModalOpen}
                                imageSrc={cropSrc}
                                mimeType={cropMimeType}
                                minAspectRatio={1 / 2}
                                maxAspectRatio={5 / 1}
                                onClose={handleCropCancel}
                                onCropComplete={handleCropComplete}
                            />
                        )}
                    </div>
                );
            }}
        </Observer>
    );
}
